// usePullToRefresh — 당겨서 새로고침 (모바일 터치 제스처)
// 스크롤 최상단에서 아래로 당기면 onRefresh 실행. 고무줄 저항 + 임계 발동.
//
// 사용:
//   const { bind, pull, refreshing } = usePullToRefresh(refreshAll);
//   <div ref={bind}> ...화면... </div>   // bind는 화면 최상위에 — 가장 가까운
//                                          // .tab-layout__content 스크롤러를 자동 탐색
//   상단에 <PullIndicator pull={pull} refreshing={refreshing} /> 로 시각 피드백.
//
// ⚠ touchmove는 { passive:false } 로 바인딩해야 preventDefault(네이티브 바운스 억제)가 먹는다.
import { useCallback, useEffect, useRef, useState } from 'react';

interface Options {
  threshold?: number; // 이 거리(px) 이상 당기고 놓으면 새로고침 발동 (기본 70)
  max?: number;       // 최대 당김 거리 — 고무줄 상한 (기본 110)
}

export function usePullToRefresh(onRefresh: () => Promise<unknown> | void, opts: Options = {}) {
  const threshold = opts.threshold ?? 70;
  const max = opts.max ?? 110;

  const [pull, setPull] = useState(0);           // 현재 당김 거리(px) — 렌더용
  const [refreshing, setRefreshing] = useState(false);

  // 렌더 사이 안정적으로 참조할 값들은 ref로 — 리스너를 1회만 바인딩하기 위함
  const scroller = useRef<HTMLElement | null>(null);
  const startY = useRef<number | null>(null);
  const active = useRef(false);                  // 이번 제스처가 pull 후보인지
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;              // 항상 최신 콜백 참조

  const apply = (v: number) => { pullRef.current = v; setPull(v); };
  const setRefreshingBoth = (v: boolean) => { refreshingRef.current = v; setRefreshing(v); };

  // ref 콜백 — 화면 최상위 노드에서 가장 가까운 스크롤 컨테이너를 찾아 저장
  const bind = useCallback((node: HTMLElement | null) => {
    scroller.current = node
      ? ((node.closest('.tab-layout__content') as HTMLElement) || node)
      : null;
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      // 스크롤 최상단에서 시작할 때만 pull 후보 (중간 스크롤 중 당김은 일반 스크롤)
      if (el.scrollTop <= 0) { startY.current = e.touches[0].clientY; active.current = true; }
      else active.current = false;
    };
    const onMove = (e: TouchEvent) => {
      if (!active.current || startY.current == null || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      // 위로 스크롤이거나 이미 스크롤이 내려간 상태면 제스처 취소
      if (dy <= 0 || el.scrollTop > 0) { active.current = false; apply(0); return; }
      e.preventDefault();                        // 네이티브 오버스크롤 바운스 억제
      apply(Math.min(max, dy * 0.5));            // 저항 0.5 — 당길수록 둔해지는 느낌
    };
    const onEnd = async () => {
      if (!active.current) return;
      active.current = false;
      startY.current = null;
      if (pullRef.current >= threshold && !refreshingRef.current) {
        setRefreshingBoth(true);
        apply(threshold);                        // 새로고침 중엔 인디케이터 자리 유지
        try { await onRefreshRef.current(); }
        finally { setRefreshingBoth(false); apply(0); }
      } else {
        apply(0);                                // 임계 미달 — 원위치
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [threshold, max]);

  return { bind, pull, refreshing, threshold };
}
